import {useEffect, useRef, useState} from 'react';
import {toast} from 'sonner';
import {CustomerSystem, CustomerUserFull} from '@/store/customerFile.ts';
import {Button} from '@/components/ui/button.tsx';
import {
    GlobeAltIcon,
    PhoneIcon,
    KeyIcon,
    UserPlusIcon,
    TrashIcon,
    PencilIcon,
    ArrowPathIcon,
} from '@heroicons/react/24/outline';
import {Copy, Check, Eye, EyeOff} from 'lucide-react';
import {InviteUserModal} from './modals/InviteUserModal.tsx';
import {Dialog, DialogContent, DialogHeader, DialogTitle, DialogBody, DialogFooter} from '@/components/ui/dialog.tsx';
import {Input} from '@/components/ui/input.tsx';
import {Label} from '@/components/ui/label.tsx';

interface Props {
    users: CustomerUserFull[];
    systems: CustomerSystem[];
    currentUserRole?: number;
    canManage?: boolean;
}

const ROLE_OPTIONS = [
    {value: 1, label: 'בעלים'},
    {value: 2, label: 'מנהל'},
    {value: 3, label: 'משתמש רגיל'},
];

function roleLabel(role: number) {
    return ROLE_OPTIONS.find((option) => option.value === role)?.label ?? 'משתמש רגיל';
}

const STATUS_PILL: Record<string, {label: string; cls: string}> = {
    active: {label: 'פעיל', cls: 'bg-green-100 text-green-800'},
    invited: {label: 'נשלחה הזמנה', cls: 'bg-amber-100 text-amber-800'},
    inactive: {label: 'לא פעיל', cls: 'bg-gray-100 text-gray-600'},
};

function getStatusKey(user: CustomerUserFull) {
    if (!user.is_active) return user.email ? 'invited' : 'inactive';
    return 'active';
}

function UserTypeIcon({type}: {type: number | null}) {
    if (type === 4) return <PhoneIcon className="w-4 h-4 text-gray-500" title="משתמש טלפוני" />;
    if (type === 5) return <UserPlusIcon className="w-4 h-4 text-gray-500" title="נשלח הזמנה" />;
    if (type === 6) return <KeyIcon className="w-4 h-4 text-gray-500" title="משתמש API" />;
    return <GlobeAltIcon className="w-4 h-4 text-gray-500" title="משתמש ווב" />;
}

function getCsrfToken() {
    const match = document.cookie.match(/csrftoken=([^;]+)/);
    return match ? match[1] : '';
}

async function readResponseMessage(response: Response) {
    try {
        const data = await response.json();
        if (typeof data?.msg === 'string' && data.msg.trim()) return data.msg;
    } catch {
        // ignore non-JSON responses
    }

    return response.ok ? 'הפעולה הושלמה' : 'הפעולה נכשלה';
}

type AccessFormProps = {
    systems: CustomerSystem[];
    currentUserRole?: number;
    role: number;
    onRoleChange: (role: number) => void;
    selectedSystemIds: number[];
    onSelectedSystemIdsChange: (ids: number[]) => void;
    disabled?: boolean;
};

function AccessFormFields({
    systems,
    currentUserRole,
    role,
    onRoleChange,
    selectedSystemIds,
    onSelectedSystemIdsChange,
    disabled,
}: AccessFormProps) {
    const isRegularUser = role === 3;
    const isManager = currentUserRole === 2;
    const roleOptions = isManager ? ROLE_OPTIONS.filter((option) => option.value !== 1) : ROLE_OPTIONS;

    function toggleSystem(systemId: number) {
        onSelectedSystemIdsChange(
            selectedSystemIds.includes(systemId)
                ? selectedSystemIds.filter((id) => id !== systemId)
                : [...selectedSystemIds, systemId],
        );
    }

    return (
        <div className="space-y-4">
            <div className="space-y-1.5">
                <Label htmlFor="user-role">רמת גישה</Label>
                <select
                    id="user-role"
                    value={role}
                    onChange={(e) => onRoleChange(Number(e.target.value))}
                    disabled={disabled}
                    className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                >
                    {roleOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                            {option.label}
                        </option>
                    ))}
                </select>
            </div>

            {isRegularUser && (
                <div className="space-y-2">
                    <Label>מערכות מורשות</Label>
                    {systems.length === 0 ? (
                        <p className="text-xs text-gray-500">אין מערכות זמינות בתיק הלקוח.</p>
                    ) : (
                        <div className="max-h-44 space-y-2 overflow-y-auto rounded-lg border border-gray-200 p-3">
                            {systems.map((system) => (
                                <label key={system.id} className="flex cursor-pointer items-center gap-2 text-sm text-gray-700">
                                    <input
                                        type="checkbox"
                                        checked={selectedSystemIds.includes(system.id)}
                                        onChange={() => toggleSystem(system.id)}
                                        disabled={disabled}
                                        className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                    />
                                    <span>{system.name}</span>
                                </label>
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

/* ── Reset password confirmation modal ─── */
function ResetPasswordModal({user, open, onClose}: {user: CustomerUserFull; open: boolean; onClose: () => void}) {
    const [busy, setBusy] = useState(false);
    const [sent, setSent] = useState(false);

    async function confirm() {
        setBusy(true);
        try {
            await fetch('/api/auth/forgot-password/', {
                method: 'POST',
                headers: {'Content-Type': 'application/json', 'X-CSRFToken': getCsrfToken()},
                body: JSON.stringify({email: user.email}),
            });
            setSent(true);
        } finally {
            setBusy(false);
        }
    }

    function handleClose() {
        setSent(false);
        onClose();
    }

    return (
        <Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
            <DialogContent dir="rtl" className="max-w-sm">
                <DialogHeader><DialogTitle>איפוס סיסמה</DialogTitle></DialogHeader>
                <DialogBody>
                    {sent ? (
                        <p className="text-sm text-green-700">קישור לאיפוס הסיסמה נשלח ל-<strong>{user.email}</strong>.</p>
                    ) : (
                        <p className="text-sm text-gray-700">האם לשלוח ל-<strong>{user.full_name ?? user.email}</strong> קישור לאיפוס הסיסמה?</p>
                    )}
                </DialogBody>
                <DialogFooter className="flex gap-2 justify-end">
                    {sent ? (
                        <Button onClick={handleClose}>סגור</Button>
                    ) : (
                        <>
                            <Button variant="outline" onClick={handleClose}>ביטול</Button>
                            <Button onClick={confirm} disabled={busy}>שלח קישור</Button>
                        </>
                    )}
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

/* ── Remove confirmation modal ─── */
function RemoveUserModal({user, open, onClose, onRemoved}: {user: CustomerUserFull; open: boolean; onClose: () => void; onRemoved: (userId: number) => void}) {
    const [busy, setBusy] = useState(false);
    async function confirm() {
        setBusy(true);
        try {
            const response = await fetch(`/api/customer-file/remove-user/${user.id}/`, {
                method: 'DELETE',
                headers: {'X-CSRFToken': getCsrfToken()},
            });
            const message = await readResponseMessage(response);

            if (response.ok) {
                toast.success(message);
                onRemoved(user.id);
                onClose();
            } else {
                toast.error(message);
            }
        } finally {
            setBusy(false);
        }
    }
    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
            <DialogContent dir="rtl" className="max-w-sm">
                <DialogHeader><DialogTitle>הסרת משתמש</DialogTitle></DialogHeader>
                <DialogBody>
                    <p className="text-sm text-gray-700">האם אתה רוצה להסיר את <strong>{user.full_name ?? user.email}</strong> מהמערכת הנוכחית?</p>
                </DialogBody>
                <DialogFooter className="flex gap-2 justify-end">
                    <Button variant="outline" onClick={onClose}>ביטול</Button>
                    <Button variant="destructive" onClick={confirm} disabled={busy}>הסר</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

/* ── Edit user modal ─── */
function EditUserModal({
    user,
    systems,
    currentUserRole,
    open,
    onClose,
    onSaved,
}: {
    user: CustomerUserFull;
    systems: CustomerSystem[];
    currentUserRole?: number;
    open: boolean;
    onClose: () => void;
    onSaved: (userId: number, updates: Partial<CustomerUserFull>) => void;
}) {
    const [phone, setPhone] = useState(user.phone ?? '');
    const [role, setRole] = useState(user.role);
    const [selectedSystemIds, setSelectedSystemIds] = useState<number[]>(user.user_schools.map((school) => school.id));
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const isManager = currentUserRole === 2;
    const userDisplayName = user.full_name || user.email || `#${user.id}`;

    useEffect(() => {
        if (!open) return;
        setPhone(user.phone ?? '');
        setRole(user.role);
        setSelectedSystemIds(user.user_schools.map((school) => school.id));
        setError('');
        setBusy(false);
    }, [open, user]);

    async function save() {
        if (isManager && user.role === 1) {
            setError('מנהל לא יכול לערוך משתמש בעלים');
            return;
        }
        if (isManager && role === 1) {
            setError('מנהל לא יכול לשנות משתמש לרמת בעלים');
            return;
        }
        if (role === 3 && selectedSystemIds.length === 0) {
            setError('יש לבחור לפחות מערכת אחת למשתמש רגיל');
            return;
        }

        setBusy(true);
        try {
            const response = await fetch(`/api/customer-file/update-user/${user.id}/`, {
                method: 'PATCH',
                headers: {'Content-Type': 'application/json', 'X-CSRFToken': getCsrfToken()},
                body: JSON.stringify({
                    phone,
                    role,
                    user_school_ids: role === 3 ? selectedSystemIds : [],
                }),
            });
            const message = await readResponseMessage(response);

            if (!response.ok) {
                toast.error(message);
                return;
            }

            toast.success(message);
            onSaved(user.id, {
                phone,
                role,
                role_display: roleLabel(role),
                user_schools: role === 3
                    ? systems.filter((system) => selectedSystemIds.includes(system.id)).map((system) => ({id: system.id, name: system.name}))
                    : [],
            });
            onClose();
        } finally {
            setBusy(false);
        }
    }
    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
            <DialogContent dir="rtl" className="max-w-sm">
                <DialogHeader><DialogTitle>עריכת משתמש</DialogTitle></DialogHeader>
                <DialogBody>
                    <div className="space-y-4">
                        <p className="text-xs text-gray-500">
                            השינוי מתבצע עבור: <strong className="text-gray-700">{userDisplayName}</strong>
                        </p>
                        <div className="space-y-1">
                            <Label>טלפון</Label>
                            <Input dir="ltr" value={phone} onChange={(e) => setPhone(e.target.value)} />
                        </div>
                        <AccessFormFields
                            systems={systems}
                            currentUserRole={currentUserRole}
                            role={role}
                            onRoleChange={setRole}
                            selectedSystemIds={selectedSystemIds}
                            onSelectedSystemIdsChange={setSelectedSystemIds}
                            disabled={busy}
                        />
                        {error && <p className="text-xs text-red-600">{error}</p>}
                    </div>
                </DialogBody>
                <DialogFooter className="flex gap-2 justify-end">
                    <Button variant="outline" onClick={onClose}>ביטול</Button>
                    <Button onClick={save} disabled={busy}>שמור</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

/* ── Copy token button ─── */
function CopyTokenButton({token}: {token: string}) {
    const [copied, setCopied] = useState(false);
    const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    function copy() {
        navigator.clipboard.writeText(token);
        setCopied(true);
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        timeoutRef.current = setTimeout(() => setCopied(false), 1500);
    }
    return (
        <button type="button" onClick={copy} className="p-1 rounded hover:bg-gray-100 text-gray-400 hover:text-gray-700 transition-colors" title="העתק טוקן">
            {copied ? <Check className="size-4 text-green-500" /> : <Copy className="size-4" />}
        </button>
    );
}

/* ── Token input with show/hide toggle ─── */
function TokenInput({value}: {value: string}) {
    const [show, setShow] = useState(false);
    return (
        <div className="flex items-center gap-1">
            <Input dir="ltr" readOnly value={show ? value : `${value.slice(0, 8)}${'•'.repeat(12)}`} className="font-mono text-xs h-8 flex-1 bg-gray-50" />
            <button type="button" onClick={() => setShow((s) => !s)} className="p-1 rounded hover:bg-gray-100 text-gray-400 hover:text-gray-700 transition-colors" title={show ? 'הסתר' : 'הצג'}>
                {show ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
            </button>
            <CopyTokenButton token={value} />
        </div>
    );
}

/* ── Create API Token modal ─── */
// קוד מסלול מרכזיות — רק מוסדות במסלול זה יכולים לקבל API Token
const API_TOKEN_SUBSCRIPTION_CODE = '3';

function CreateApiTokenModal({
    systems,
    open,
    onClose,
    onCreated,
}: {
    systems: CustomerSystem[];
    open: boolean;
    onClose: () => void;
    onCreated: (user: CustomerUserFull) => void;
}) {
    const merkazia = systems.filter((s) => s.active && s.subscription_code === API_TOKEN_SUBSCRIPTION_CODE);
    const [schoolId, setSchoolId] = useState<string>('');
    const [busy, setBusy] = useState(false);
    const [createdToken, setCreatedToken] = useState<string | null>(null);
    const [error, setError] = useState('');

    useEffect(() => {
        if (!open) return;
        setSchoolId('');
        setBusy(false);
        setCreatedToken(null);
        setError('');
    }, [open]);

    async function create() {
        if (!schoolId) { setError('יש לבחור מערכת'); return; }
        setBusy(true);
        setError('');
        try {
            const res = await fetch('/api/customer-file/create-api-token/', {
                method: 'POST',
                headers: {'Content-Type': 'application/json', 'X-CSRFToken': getCsrfToken()},
                body: JSON.stringify({school_id: Number(schoolId)}),
            });
            const data = await res.json();
            if (!res.ok) { setError(data?.msg || 'שגיאה ביצירת הטוקן'); return; }
            setCreatedToken(data?.data?.api_token ?? '');
            onCreated(data?.data as CustomerUserFull);
            toast.success('הטוקן נוצר בהצלחה');
        } finally {
            setBusy(false);
        }
    }

    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
            <DialogContent dir="rtl" className="max-w-sm">
                <DialogHeader><DialogTitle>יצירת טוקן API</DialogTitle></DialogHeader>
                <DialogBody>
                    {createdToken ? (
                        <div className="space-y-3">
                            <p className="text-sm text-green-700 font-medium">הטוקן נוצר! שמור אותו במקום בטוח.</p>
                            <div className="space-y-1">
                                <Label>טוקן</Label>
                                <TokenInput value={createdToken} />
                            </div>
                            <p className="text-xs text-gray-500">הטוקן יוצג שוב רק דרך עמוד הגדרות הטוקן.</p>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            <p className="text-sm text-gray-600">יצירת משתמש API חדש המשויך למערכת אחת.</p>
                            <div className="space-y-1.5">
                                <Label htmlFor="api-school">מערכת (מסלול מרכזיות בלבד)</Label>
                                {merkazia.length === 0 ? (
                                    <p className="text-xs text-amber-600">אין מוסדות פעילים במסלול מרכזיות בתיק לקוח זה</p>
                                ) : (
                                    <select
                                        id="api-school"
                                        value={schoolId}
                                        onChange={(e) => setSchoolId(e.target.value)}
                                        disabled={busy}
                                        className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                                    >
                                        <option value="">בחר מערכת...</option>
                                        {merkazia.map((s) => (
                                            <option key={s.id} value={s.id}>{s.name}</option>
                                        ))}
                                    </select>
                                )}
                            </div>
                            {error && <p className="text-xs text-red-600">{error}</p>}
                        </div>
                    )}
                </DialogBody>
                <DialogFooter className="flex gap-2 justify-end">
                    {createdToken ? (
                        <Button onClick={onClose}>סגור</Button>
                    ) : (
                        <>
                            <Button variant="outline" onClick={onClose}>ביטול</Button>
                            <Button onClick={create} disabled={busy}>צור טוקן</Button>
                        </>
                    )}
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

/* ── Edit API Token modal ─── */
function EditApiTokenModal({
    user,
    systems,
    open,
    onClose,
    onSaved,
}: {
    user: CustomerUserFull;
    systems: CustomerSystem[];
    open: boolean;
    onClose: () => void;
    onSaved: (userId: number, updates: Partial<CustomerUserFull>) => void;
}) {
    const merkazia = systems.filter((s) => s.active && s.subscription_code === API_TOKEN_SUBSCRIPTION_CODE);
    const [schoolId, setSchoolId] = useState<string>('');
    const [token, setToken] = useState<string>('');
    const [loadingToken, setLoadingToken] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (!open) return;
        setSchoolId(String(user.user_schools[0]?.id ?? ''));
        setError('');
        setBusy(false);

        // Fetch full token
        setLoadingToken(true);
        fetch(`/api/customer-file/api-token/${user.id}/`, {
            headers: {'X-CSRFToken': getCsrfToken()},
        })
            .then((r) => r.json())
            .then((d) => setToken(d?.data?.api_token ?? ''))
            .catch(() => setToken(''))
            .finally(() => setLoadingToken(false));
    }, [open, user]);

    async function save() {
        if (!schoolId) { setError('יש לבחור מערכת'); return; }
        setBusy(true);
        setError('');
        try {
            const res = await fetch(`/api/customer-file/api-token/${user.id}/update/`, {
                method: 'PATCH',
                headers: {'Content-Type': 'application/json', 'X-CSRFToken': getCsrfToken()},
                body: JSON.stringify({school_id: Number(schoolId)}),
            });
            const data = await res.json();
            if (!res.ok) { setError(data?.msg || 'שגיאה בעדכון'); return; }
            toast.success('הטוקן עודכן');
            onSaved(user.id, {
                user_schools: systems.filter((s) => s.id === Number(schoolId)).map((s) => ({id: s.id, name: s.name})),
            });
            onClose();
        } finally {
            setBusy(false);
        }
    }

    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
            <DialogContent dir="rtl" className="max-w-sm">
                <DialogHeader><DialogTitle>עריכת טוקן API</DialogTitle></DialogHeader>
                <DialogBody>
                    <div className="space-y-4">
                        <div className="space-y-1">
                            <Label>טוקן</Label>
                            {loadingToken ? (
                                <p className="text-xs text-gray-400">טוען...</p>
                            ) : (
                                <TokenInput value={token} />
                            )}
                        </div>
                        <div className="space-y-1.5">
                            <Label htmlFor="edit-api-school">מערכת (מסלול מרכזיות בלבד)</Label>
                            {merkazia.length === 0 ? (
                                <p className="text-xs text-amber-600">אין מוסדות פעילים במסלול מרכזיות</p>
                            ) : (
                                <select
                                    id="edit-api-school"
                                    value={schoolId}
                                    onChange={(e) => setSchoolId(e.target.value)}
                                    disabled={busy}
                                    className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                                >
                                    <option value="">בחר מערכת...</option>
                                    {merkazia.map((s) => (
                                        <option key={s.id} value={s.id}>{s.name}</option>
                                    ))}
                                </select>
                            )}
                        </div>
                        {error && <p className="text-xs text-red-600">{error}</p>}
                    </div>
                </DialogBody>
                <DialogFooter className="flex gap-2 justify-end">
                    <Button variant="outline" onClick={onClose}>ביטול</Button>
                    <Button onClick={save} disabled={busy}>שמור</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function UserRow({
    user,
    systems,
    currentUserRole,
    onRemoved,
    onUpdated,
}: {
    user: CustomerUserFull;
    systems: CustomerSystem[];
    currentUserRole?: number;
    onRemoved: (userId: number) => void;
    onUpdated: (userId: number, updates: Partial<CustomerUserFull>) => void;
}) {
    const [removeOpen, setRemoveOpen] = useState(false);
    const [editOpen, setEditOpen] = useState(false);
    const [editApiOpen, setEditApiOpen] = useState(false);
    const [resetOpen, setResetOpen] = useState(false);
    const statusKey = getStatusKey(user);
    const status = STATUS_PILL[statusKey];
    const schoolsLabel = user.user_schools.map((s) => s.name).join(', ') || '—';

    const isWebUser = user.type !== null && user.type <= 2;
    const isPhoneUser = user.type === 4;
    const isApiUser = user.type === 6;
    const canResetPassword = statusKey === 'active';
    const isManager = currentUserRole === 2;
    const isOwnerUser = user.role === 1;
    const managerBlocked = isManager && isOwnerUser;

    return (
        <>
            <tr className="border-b border-gray-50 hover:bg-gray-50/50">
                <td className="py-3 px-3 text-center">
                    <UserTypeIcon type={user.type} />
                </td>
                <td className="py-3 px-3">
                    <span className="text-sm font-medium text-gray-900">{user.full_name ?? '—'}</span>
                </td>
                <td className="py-3 px-3 text-sm text-gray-600">{user.email ?? '—'}</td>
                <td className="py-3 px-3 text-sm text-gray-600 font-mono">{user.phone ?? '—'}</td>
                <td className="py-3 px-3 text-sm text-gray-600">{user.role_display}</td>
                <td className="py-3 px-3">
                    <span className="text-xs text-gray-500 max-w-[140px] truncate block" title={schoolsLabel}>
                        {schoolsLabel}
                    </span>
                </td>
                <td className="py-3 px-3">
                    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${status.cls}`}>
                        {status.label}
                    </span>
                </td>
                <td className="py-3 px-3">
                    <div className="flex items-center gap-1">
                        {(isWebUser || isPhoneUser) && (
                            <button
                                type="button"
                                title={managerBlocked ? 'מנהל לא יכול לערוך משתמש בעלים' : 'עריכה'}
                                onClick={() => !managerBlocked && setEditOpen(true)}
                                disabled={managerBlocked}
                                className="inline-flex items-center justify-center w-7 h-7 rounded-lg border border-gray-200 text-gray-500 hover:text-blue-600 hover:border-blue-300 transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:text-gray-500 disabled:hover:border-gray-200"
                            >
                                <PencilIcon className="w-3.5 h-3.5" />
                            </button>
                        )}
                        {isApiUser && (
                            <button
                                type="button"
                                title="שינוי מערכת משויכת"
                                onClick={() => setEditApiOpen(true)}
                                className="inline-flex items-center justify-center w-7 h-7 rounded-lg border border-gray-200 text-gray-500 hover:text-blue-600 hover:border-blue-300 transition-colors"
                            >
                                <PencilIcon className="w-3.5 h-3.5" />
                            </button>
                        )}
                        {isWebUser && (
                            <button
                                type="button"
                                title={canResetPassword ? 'איפוס סיסמה' : 'המשתמש אינו פעיל'}
                                onClick={() => canResetPassword && !managerBlocked && setResetOpen(true)}
                                disabled={!canResetPassword || managerBlocked}
                                className="inline-flex items-center justify-center w-7 h-7 rounded-lg border border-gray-200 text-gray-500 hover:text-amber-600 hover:border-amber-300 transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:text-gray-500 disabled:hover:border-gray-200"
                            >
                                <ArrowPathIcon className="w-3.5 h-3.5" />
                            </button>
                        )}
                        {isPhoneUser && (
                            <button
                                type="button"
                                title="חיבור מחדש"
                                onClick={() => !managerBlocked && setResetOpen(true)}
                                disabled={managerBlocked}
                                className="inline-flex items-center justify-center w-7 h-7 rounded-lg border border-gray-200 text-gray-500 hover:text-emerald-600 hover:border-emerald-300 transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:text-gray-500 disabled:hover:border-gray-200"
                            >
                                <ArrowPathIcon className="w-3.5 h-3.5" />
                            </button>
                        )}
                        <button
                            type="button"
                            title={managerBlocked ? 'מנהל לא יכול להסיר משתמש בעלים' : 'הסרת משתמש'}
                            onClick={() => !managerBlocked && setRemoveOpen(true)}
                            disabled={managerBlocked}
                            className="inline-flex items-center justify-center w-7 h-7 rounded-lg border border-red-100 text-red-400 hover:text-red-600 hover:border-red-300 transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:text-red-400 disabled:hover:border-red-100"
                        >
                            <TrashIcon className="w-3.5 h-3.5" />
                        </button>
                    </div>
                </td>
            </tr>
            <ResetPasswordModal user={user} open={resetOpen} onClose={() => setResetOpen(false)} />
            <RemoveUserModal
                user={user}
                open={removeOpen}
                onClose={() => setRemoveOpen(false)}
                onRemoved={onRemoved}
            />
            <EditUserModal
                user={user}
                systems={systems}
                currentUserRole={currentUserRole}
                open={editOpen}
                onClose={() => setEditOpen(false)}
                onSaved={onUpdated}
            />
            <EditApiTokenModal
                user={user}
                systems={systems}
                open={editApiOpen}
                onClose={() => setEditApiOpen(false)}
                onSaved={onUpdated}
            />
        </>
    );
}

export function CustomerFileUsers({users, systems, currentUserRole, canManage}: Props) {
    const [inviteOpen, setInviteOpen] = useState(false);
    const [createTokenOpen, setCreateTokenOpen] = useState(false);
    const [visibleUsers, setVisibleUsers] = useState(users);

    useEffect(() => {
        setVisibleUsers(users);
    }, [users]);

    function handleRemoveUser(userId: number) {
        setVisibleUsers((current) => current.filter((user) => user.id !== userId));
    }

    function handleUpdateUser(userId: number, updates: Partial<CustomerUserFull>) {
        setVisibleUsers((current) => current.map((user) => (user.id === userId ? {...user, ...updates} : user)));
    }

    function handleInviteCreated(user: CustomerUserFull) {
        setVisibleUsers((current) => {
            if (current.some((existing) => existing.id === user.id)) {
                return current.map((existing) => (existing.id === user.id ? {...existing, ...user} : existing));
            }

            return [...current, user];
        });
    }

    return (
        <div>
            <div className="flex items-center gap-2 mb-4 flex-wrap">
                <Button
                    size="sm"
                    className="inline-flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700"
                    onClick={() => setInviteOpen(true)}
                >
                    <UserPlusIcon className="w-4 h-4" />
                    הזמנת משתמש
                </Button>
                <Button
                    size="sm"
                    variant="outline"
                    className="inline-flex items-center gap-1.5 border-amber-300 text-amber-700 hover:bg-amber-50"
                    onClick={() => setCreateTokenOpen(true)}
                >
                    <KeyIcon className="w-4 h-4" />
                    יצירת טוקן API
                </Button>
            </div>

            {visibleUsers.length === 0 ? (
                <p className="text-sm text-gray-400 py-6 text-center">אין משתמשים רשומים</p>
            ) : (
                <div className="overflow-x-auto">
                    <table className="w-full text-right text-sm">
                        <thead>
                        <tr className="border-b border-gray-100">
                            <th className="py-2 px-3 text-xs font-medium text-gray-500 text-center">סוג</th>
                            <th className="py-2 px-3 text-xs font-medium text-gray-500 text-right">שם משתמש</th>
                            <th className="py-2 px-3 text-xs font-medium text-gray-500 text-right">מייל</th>
                            <th className="py-2 px-3 text-xs font-medium text-gray-500 text-right">טלפון</th>
                            <th className="py-2 px-3 text-xs font-medium text-gray-500 text-right">תפקיד</th>
                            <th className="py-2 px-3 text-xs font-medium text-gray-500 text-right">מערכות מורשות</th>
                            <th className="py-2 px-3 text-xs font-medium text-gray-500 text-right">סטטוס</th>
                            <th className="py-2 px-3 text-xs font-medium text-gray-500 text-right">פעולות</th>
                        </tr>
                        </thead>
                        <tbody>
                        {visibleUsers.map((user) => (
                            <UserRow key={user.id} user={user} systems={systems} currentUserRole={currentUserRole} onRemoved={handleRemoveUser} onUpdated={handleUpdateUser} />
                        ))}
                        </tbody>
                    </table>
                </div>
            )}

            <InviteUserModal
                open={inviteOpen}
                systems={systems}
                currentUserRole={currentUserRole}
                onClose={() => setInviteOpen(false)}
                onCreated={handleInviteCreated}
            />
            <CreateApiTokenModal
                systems={systems}
                open={createTokenOpen}
                onClose={() => setCreateTokenOpen(false)}
                onCreated={handleInviteCreated}
            />
        </div>
    );
}
